import { useMemo } from 'react';
import {
  ALL_DIMENSIONS,
  DIMENSION_DESCRIPTIONS,
  DIMENSION_LABELS,
  LEVEL_LABELS,
  RiskAnalysis,
  RiskDimensionId,
  RiskLevel,
} from '../types/risk';
import './RiskIndicator.css';

interface RiskIndicatorProps {
  analysis: RiskAnalysis | null;
  /** Compact variant for list cells; renders 5 mini-bars instead of radar. */
  compact?: boolean;
  /** Skip rendering when IAL is low and no dimensions are alert-level. */
  hideWhenLow?: boolean;
}

const RADAR_SIZE = 220;
const RADAR_CENTER = RADAR_SIZE / 2;
const RADAR_RADIUS = RADAR_CENTER - 28;

function levelColor(level: RiskLevel): string {
  if (level === 'high') return 'var(--alert-strong)';
  if (level === 'medium') return 'var(--steel)';
  return 'var(--steel-soft)';
}

function levelLabel(level: RiskLevel): string {
  return LEVEL_LABELS[level];
}

function polarPoint(
  centerX: number,
  centerY: number,
  radius: number,
  angleDeg: number,
): { x: number; y: number } {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleRad),
    y: centerY + radius * Math.sin(angleRad),
  };
}

interface RadarProps {
  dimensions: Record<RiskDimensionId, { score: number; level: RiskLevel }>;
}

function RiskRadar({ dimensions }: RadarProps) {
  const angleStep = 360 / ALL_DIMENSIONS.length;

  const axes = useMemo(
    () =>
      ALL_DIMENSIONS.map((id, idx) => {
        const angle = idx * angleStep;
        const outer = polarPoint(RADAR_CENTER, RADAR_CENTER, RADAR_RADIUS, angle);
        const label = polarPoint(
          RADAR_CENTER,
          RADAR_CENTER,
          RADAR_RADIUS + 18,
          angle,
        );
        return { id, angle, outer, label };
      }),
    [angleStep],
  );

  const gridRings = [0.25, 0.5, 0.75, 1];

  const polygonPoints = ALL_DIMENSIONS.map((id, idx) => {
    const angle = idx * angleStep;
    const radius = (Math.min(100, dimensions[id]?.score ?? 0) / 100) * RADAR_RADIUS;
    const p = polarPoint(RADAR_CENTER, RADAR_CENTER, radius, angle);
    return `${p.x},${p.y}`;
  }).join(' ');

  return (
    <svg
      className="risk-radar"
      viewBox={`0 0 ${RADAR_SIZE} ${RADAR_SIZE}`}
      role="img"
      aria-label="Gráfico de radar com cinco dimensões de risco legislativo"
    >
      {gridRings.map((ratio) => (
        <circle
          key={ratio}
          cx={RADAR_CENTER}
          cy={RADAR_CENTER}
          r={RADAR_RADIUS * ratio}
          className="risk-radar-grid"
        />
      ))}
      {axes.map((axis) => (
        <line
          key={`axis-${axis.id}`}
          x1={RADAR_CENTER}
          y1={RADAR_CENTER}
          x2={axis.outer.x}
          y2={axis.outer.y}
          className="risk-radar-axis"
        />
      ))}
      <polygon points={polygonPoints} className="risk-radar-shape" />
      {ALL_DIMENSIONS.map((id, idx) => {
        const angle = idx * angleStep;
        const score = Math.min(100, dimensions[id]?.score ?? 0);
        const radius = (score / 100) * RADAR_RADIUS;
        const p = polarPoint(RADAR_CENTER, RADAR_CENTER, radius, angle);
        return (
          <circle
            key={`dot-${id}`}
            cx={p.x}
            cy={p.y}
            r={4}
            className={`risk-radar-dot risk-radar-dot-${dimensions[id]?.level ?? 'low'}`}
          />
        );
      })}
      {axes.map((axis) => (
        <text
          key={`label-${axis.id}`}
          x={axis.label.x}
          y={axis.label.y}
          className="risk-radar-label"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {DIMENSION_LABELS[axis.id]}
        </text>
      ))}
    </svg>
  );
}

interface MiniBarsProps {
  dimensions: Record<RiskDimensionId, { score: number; level: RiskLevel }>;
}

function RiskMiniBars({ dimensions }: MiniBarsProps) {
  return (
    <div className="risk-minibars" role="img" aria-label="Barras com cinco dimensões de risco">
      {ALL_DIMENSIONS.map((id) => {
        const dim = dimensions[id];
        const score = Math.min(100, dim?.score ?? 0);
        return (
          <div
            key={id}
            className="risk-minibar"
            title={`${DIMENSION_LABELS[id]} — ${score} (${levelLabel(dim?.level ?? 'low')})`}
          >
            <span className="risk-minibar-label">{DIMENSION_LABELS[id]}</span>
            <div className="risk-minibar-track">
              <div
                className={`risk-minibar-fill risk-minibar-fill-${dim?.level ?? 'low'}`}
                style={{ width: `${score}%` }}
              />
            </div>
            <span className="risk-minibar-score">{score}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function RiskIndicator({
  analysis,
  compact = false,
  hideWhenLow = false,
}: RiskIndicatorProps) {
  if (!analysis) return null;
  if (hideWhenLow && analysis.ialLevel === 'low') return null;

  const simplified = Object.fromEntries(
    ALL_DIMENSIONS.map((id) => [
      id,
      {
        score: analysis.dimensions[id]?.score ?? 0,
        level: analysis.dimensions[id]?.level ?? 'baixo',
      },
    ]),
  ) as Record<RiskDimensionId, { score: number; level: RiskLevel }>;

  if (compact) {
    return (
      <div className={`risk-indicator-compact risk-level-${analysis.ialLevel}`}>
        <span className="risk-indicator-compact-score">{analysis.ialScore}</span>
        <span className="risk-indicator-compact-label">IAL</span>
        <span className="risk-indicator-compact-level">
          {levelLabel(analysis.ialLevel)}
        </span>
      </div>
    );
  }

  return (
    <div className="risk-indicator">
      <header className="risk-indicator-header">
        <div className="risk-indicator-headline">
          <span className="risk-indicator-label">
            Índice de Atenção Legislativa
          </span>
          <span className="risk-indicator-score" style={{ color: levelColor(analysis.ialLevel) }}>
            {analysis.ialScore}
            <span className="risk-indicator-score-suffix">/100</span>
          </span>
          <span className={`risk-indicator-pill risk-level-${analysis.ialLevel}`}>
            {levelLabel(analysis.ialLevel)}
          </span>
        </div>
        {analysis.summary && (
          <p className="risk-indicator-summary">{analysis.summary}</p>
        )}
      </header>

      <div className="risk-indicator-visual">
        <RiskRadar dimensions={simplified} />
        <RiskMiniBars dimensions={simplified} />
      </div>

      <ul className="risk-indicator-legend">
        {ALL_DIMENSIONS.map((id) => (
          <li key={id} className="risk-indicator-legend-item">
            <span
              className={`risk-indicator-legend-dot risk-level-${
                simplified[id]?.level ?? 'low'
              }`}
              aria-hidden
            />
            <span className="risk-indicator-legend-label">
              {DIMENSION_LABELS[id]}
            </span>
            <span className="risk-indicator-legend-desc">
              {DIMENSION_DESCRIPTIONS[id]}
            </span>
            <span className="risk-indicator-legend-score">
              {simplified[id]?.score ?? 0}
            </span>
          </li>
        ))}
      </ul>

      <footer className="risk-indicator-footer">
        <p className="risk-indicator-disclaimer">{analysis.disclaimer}</p>
      </footer>
    </div>
  );
}
